import { Typography } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers/navigation";
import { RobotoButton } from "@/shared/components";
import { IframeContent } from "@/shared/components/IframeContent";
import {
  IngestionStatus,
  MAX_FILE_SIZE_PREVIEW,
  FileRecord,
} from "@/shared/domain/files/FileRecord";
import {
  isImage,
  isText,
  isMarkdown,
  isVideo,
  isHtml,
  isPdf,
  canHaveTopics,
} from "@/shared/domain/files/fileTypes";
import {
  useFileSuspense,
  useSignedUrl,
} from "@/shared/domain/files/hooks/useFile";
import { useRegisterViewingContext } from "@/shared/services/ViewingContextService/useViewingContext";

import styles from "./FileContent.module.css";
import { ImageContent } from "./ImageContent";
import { TextContent } from "./TextContent";
import { VideoContent } from "./VideoContent";

interface FileContentProps {
  fileId: string;
}

export function FileContent({ fileId }: FileContentProps) {
  const { goto } = useNavigation();

  const { data: fileRecord } = useFileSuspense(fileId);

  const signedUrlQuery = useSignedUrl(fileRecord);

  const contentType = getContentType(fileRecord);

  useRegisterViewingContext("file", {
    fileId,
    record: fileRecord,
  });

  switch (contentType) {
    case "image":
      return (
        <ImageContent fileRecord={fileRecord} signedUrl={signedUrlQuery.data} />
      );

    case "video":
      return (
        <VideoContent fileRecord={fileRecord} signedUrl={signedUrlQuery.data} />
      );

    case "text":
      return (
        <TextContent fileRecord={fileRecord} signedUrl={signedUrlQuery.data} />
      );

    case "text-too-large":
      return (
        <MsgContent>
          File size is over 50 MB, skipping preview <br />
          <span className={styles.secondaryText}>
            Open or Download the file using the buttons above
          </span>
        </MsgContent>
      );

    case "html":
      return (
        <IframeContent
          signedUrl={signedUrlQuery.data}
          // Sandbox restricts what embedded content can do.
          // See https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/iframe#sandbox
          //
          // allow-scripts: Customer HTML files (e.g. analytics reports) contain JS
          //   for charts, maps, and interactive tables.
          // allow-same-origin: Without this, the iframe gets an opaque origin which
          //   strips the Referer header from sub-resource requests. OpenStreetMap tile
          //   servers (used by Plotly maps) require a Referer and return 403 without one.
          //   This is safe because the iframe's origin (S3) differs from the app's origin,
          //   so it cannot access the app's cookies, storage, or DOM.
          // allow-popups: Customer reports contain target="_blank" links (e.g. links back
          //   to the app). Without this, clicking those links silently fails.
          // allow-popups-to-escape-sandbox: Opened tabs (e.g. links back to Roboto URLs)
          //   should not inherit the sandbox restrictions, which would break the app.
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        />
      );

    case "pdf":
      // No sandbox for PDFs — Chrome's PDF viewer doesn't activate in sandboxed
      // iframes (https://issues.chromium.org/issues/41131921). Cross-origin isolation
      // (S3 origin != app origin) prevents access to app cookies, storage, and DOM regardless.
      return <IframeContent signedUrl={signedUrlQuery.data} />;

    case "no-preview-ingested":
      return (
        <MsgContent>
          No preview available for this file type.
          <br />
          <span className={styles.secondaryText}>
            <RobotoButton
              eventName={"VisualizeFileClicked"}
              variant={"text"}
              color="primary"
              onClick={() => {
                goto.visualize({
                  dataSource: {
                    files: [fileRecord.file_id],
                  },
                });
              }}
              eventProperties={{
                fileId: fileRecord.file_id,
              }}
            >
              Open in Visualizer
            </RobotoButton>
          </span>
        </MsgContent>
      );

    case "not-ingested":
      return (
        <MsgContent>
          File not ingested yet
          <br />
          <span className={styles.secondaryText}>
            This file hasn&apos;t been ingested, so there aren&apos;t any topics
            available for visualization. Check if any ingestion actions are
            currently running on its dataset, or invoke one manually.
          </span>
        </MsgContent>
      );

    case "no-preview":
      return (
        <MsgContent>
          No preview available <br />
          <span className={styles.secondaryText}>
            Open or Download the file using the buttons above
          </span>
        </MsgContent>
      );
  }
}

type ContentType =
  | "image"
  | "video"
  | "text-too-large"
  | "text"
  | "html"
  | "pdf"
  | "no-preview-ingested"
  | "not-ingested"
  | "no-preview";

function getContentType(fileRecord: FileRecord): ContentType {
  if (isImage(fileRecord.relative_path)) {
    return "image";
  }
  if (isVideo(fileRecord.relative_path)) {
    return "video";
  }
  if (
    isText(fileRecord.relative_path) ||
    isMarkdown(fileRecord.relative_path)
  ) {
    return fileRecord.size > MAX_FILE_SIZE_PREVIEW ? "text-too-large" : "text";
  }
  if (isHtml(fileRecord.relative_path)) {
    return "html";
  }
  if (isPdf(fileRecord.relative_path)) {
    return "pdf";
  }
  if (fileRecord.ingestion_status !== IngestionStatus.NotIngested) {
    return "no-preview-ingested";
  }
  if (canHaveTopics(fileRecord.relative_path)) {
    return "not-ingested";
  }
  return "no-preview";
}

function MsgContent({ children }: React.PropsWithChildren) {
  return (
    <div className={styles.msgContentContainer}>
      <Typography className={styles.msgContentText}>{children}</Typography>
    </div>
  );
}
